import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed } from '@legendapp/state/react'
import { useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
} from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteIntegrator, fetchIntegratorList } from '~features/integrator/api'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { fetchAllDivision, fetchDistributorList } from '~in-app/shareable/index.api'
import { DistributorRoot, Division, Integrator } from '~in-app/shareable/index.types'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/integrator/list')({
  component: IntegratorPage,
  loader: async () => {
    let distributorIds: Option[] = []
    let divisionIds: Option[] = []
    const privilegeValue = auth$.privileges.get()
    const defaultIntegratorListing = privilegeValue.has('0.3.0.0')
      ? await fetchIntegratorList({
        query: {
          divisionIds: new URLSearchParams(window.location.search).get('divisionIds') || undefined,
          clientIds: new URLSearchParams(window.location.search).get('distributorIds') || undefined,
          queries: new URLSearchParams(window.location.search).get('queries') || undefined,
          page: (() => {
            const match = new URLSearchParams(window.location.search).get('page')
            return match ? parseInt(match, 10) - 1 : 0
          })(),
          size: (() => {
            const match = new URLSearchParams(window.location.search).get('pageSize')
            return match ? parseInt(match, 10) : 20
          })(),
          sort: (defaultState$.sortList.get() || []).map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`).join(','),
        },
      })
      : {} as ListingResponse<Integrator>

    const distributorResponse = privilegeValue?.has('0.3.7.0')
      ? await fetchDistributorList()
      : {} as ListingResponse<DistributorRoot>
    distributorIds = distributorResponse
      ? distributorResponse?.list?.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
      : []

    const defaultDivisionList = privilegeValue.has('0.4.0.0')
      ? await fetchAllDivision()
      : {} as { divisions: Division[] }
    const flattenDivisions = (
      divisions: any[],
      parentId: string | null = null,
    ): any[] => {
      return divisions.reduce((acc: any[], division: any) => {
        acc.push({
          value: division.id,
          label: division.name,
          parentId: parentId,
        })
        if (division.mychilds && division.mychilds.length > 0) {
          acc = acc.concat(flattenDivisions(division.mychilds, division.id))
        }
        return acc
      }, [])
    }
    divisionIds = flattenDivisions(defaultDivisionList.divisions)

    // Ensure that the final return contains the fetched values.
    return { distributorIds, divisionIds, privilegeValue, defaultIntegratorListing }
  },
  staticData: {
    privileges: ['0.3.0.0'],
  },
})
const columnHelper = createColumnHelper<Integrator>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  divisionIds: string[] | undefined
  distributorIds: string[] | undefined
  isChange: boolean
  deleteIntegratorId: string | undefined
  isOpenDeleteIntegrator: boolean
  deleteIntegratorName: string | undefined
}>({
  sortList: [
    { id: 'name', desc: true },
  ],
  pagination: 0,
  pageSize: 20,
  queries: '',
  divisionIds: undefined,
  distributorIds: undefined,
  isChange: false,
  deleteIntegratorId: undefined,
  isOpenDeleteIntegrator: false,
  deleteIntegratorName: undefined,
})
function IntegratorPage() {
  const { distributorIds, divisionIds, privilegeValue, defaultIntegratorListing } = useLoaderData({
    from: '/console/integrator/list',
  })
  const navigate = Route.useNavigate()
  const dropdownItems = (value: Integrator) => {
    return [
      {
        label: 'Edit',
        onClick: () => navigate({ to: `/console/integrator/edit/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.0.2'),
        icon: (
          <Icon
            height="16"
            icon="lucide:edit-2"
            width="16"
          />
        ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteItem(value),
        disabled: !privilegeValue?.has('0.3.0.3'),
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('division.name', {
      cell: info => info.getValue(),
      header: () => <span>Division</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable(defaultState$)

  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.distributorIds.set(new URLSearchParams(window.location.search).get('distributorIds')?.split(','))
    state$.divisionIds.set(new URLSearchParams(window.location.search).get('divisionIds')?.split(','))
    state$.queries.set(filteredQueries)
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])
  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',

    },
    ...(privilegeValue?.has('0.3.7.0')
      ? [{
          label: 'Distributors',
          type: 'dropdown' as const,
          isMultiSelect: true,
          options: distributorIds,
          value: distributorIds.filter(distributor => new URLSearchParams(window.location.search).get('distributorIds')?.split(',').includes(distributor.value)),
          result: distributorIds.filter(distributor => new URLSearchParams(window.location.search).get('distributorIds')?.split(',').includes(distributor.value)),
        }]
      : []),
    ...(auth$.clientId.get() && privilegeValue?.has('0.4.0.0')
      ? [{
          label: 'Divisions',
          type: 'dropdown' as const,
          isMultiSelect: true,
          options: divisionIds,
          value: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
          result: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
        }]
      : []),
  ]

  const tableState$ = useObservable<TableState$<Integrator>>(() => ({
    id: 'integrator-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pageSize: state$.pageSize.get(),
    pagination: state$.pagination.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}
      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      const divisionFilter = value.find(
        (item: Option) => item.label === 'Divisions',
      )
      const divisionValue = divisionFilter?.result || []
      if (divisionValue.length > 0) {
        state$.divisionIds.set(
          divisionValue.map((item: { value: any }) => item.value),
        )
        searchParams.divisionIds = divisionValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        searchParams.divisionIds = undefined
        state$.divisionIds.set(undefined)
      }
      const integratorFilter = value.find(
        (item: Option) => item.label === 'Distributors',
      )
      const integratorValue = integratorFilter?.result || []
      if (integratorValue.length > 0) {
        state$.distributorIds.set(
          integratorValue.map((item: { value: any }) => item.value),
        )
        searchParams.distributorIds = integratorValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        searchParams.distributorIds = undefined
        state$.distributorIds.set(undefined)
      }
      state$.isChange.set(!state$.isChange.get())
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(divisionValue.length > 0)) delete updatedSearch.divisionIds
          if (!(integratorValue.length > 0)) delete updatedSearch.distributorIds

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, page: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['integrator-listing'],
    queryFn: async ({ signal }) => {
      const response = await fetchIntegratorList({
        // abortCtrl: signal,
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          clientIds: state$?.distributorIds?.get()
            ? (state$?.distributorIds?.get() ?? []).join(',')
            : undefined,
          divisionIds: state$?.divisionIds?.get()
            ? (state$?.divisionIds?.get() ?? []).join(',')
            : undefined,
          sort: state$.sortList
            .get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newIntegratorList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      })) as Integrator[]
      tableState$.data.set(newIntegratorList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
    initialData: defaultIntegratorListing,
  })

  const useDeleteIntegrator = () => {
    const mutation = useMutation({
      mutationFn: deleteIntegrator,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['integrator-listing'] })
        toast.success('Integrator deleted successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteIntegrator()

  const handleDeleteItem = (value: Integrator) => {
    state$.deleteIntegratorId.set(value.id)
    state$.isOpenDeleteIntegrator.set(true)
    state$.deleteIntegratorName.set(value.name)
  }

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> System Integrator Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteIntegrator.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteIntegratorName.get()}' Integrator?`}
                onClose={() => { state$.isOpenDeleteIntegrator.set(false), state$.deleteIntegratorName.set(undefined), state$.deleteIntegratorId.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteIntegratorId = state$.deleteIntegratorId.get()
                    if (deleteIntegratorId) {
                      deleteMutation.mutate(deleteIntegratorId as string)
                      state$.deleteIntegratorId.set(undefined)
                      state$.isOpenDeleteIntegrator.set(false)
                      state$.deleteIntegratorName.set(undefined)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!auth$.privileges.get()?.has('0.3.0.1')}
          onClick={() => navigate({ to: '/console/integrator/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
